import { preventNonNumericInput } from '../../helpers/utils';

export default class QuantitySelector {

  private readonly decrementButton: JQuery;
  protected readonly input: JQuery;
  private readonly incrementButton: JQuery;
  private readonly messageContainer: JQuery;

  constructor(protected container: JQuery) {
    this.decrementButton = container.find('.js-qty-selector-minus');
    this.input = container.find('.js-qty-selector-input');
    this.incrementButton = container.find('.js-qty-selector-plus');
    this.messageContainer = container.find('.js-validationMessage');

    if (this.isAtMinimumValue()) {
      this.decrementButton.prop('disabled', true);
    }
    if (this.input.val() === this.input.data('max')) {
      this.incrementButton.prop('disabled', true);
    }

    this.decrementButton.on('click', () => this.checkQtySelector('minus'));
    this.incrementButton.on('click', () => this.checkQtySelector('plus'));

    this.input.on('keypress', (e: Event) => {
      preventNonNumericInput(e);
    });

    this.input.on('keydown', (e) => {
      if (e.which === 38) { // Up key
        this.checkQtySelector('plus');
      } else if (e.which === 40) { // Down key
        this.checkQtySelector('minus');
      } else if (e.which === 13) {
        this.checkQtySelector('blur');
      }
    });

    this.input.on('blur', () => {
      this.checkQtySelector('blur');
    });

    this.checkQtySelector('blur');
    this.container.data('quantity-selector-instance', this);
  }

  static init(element: JQuery): QuantitySelector | void {
    if (element.data('quantity-selector-instance')) {
      return element.data('quantity-selector') as QuantitySelector;
    }
    return new QuantitySelector(element);
  }

  static initAll(container?: JQuery): void {
    if (container) {
      container.find('.js-qty-selector').map((i, el) => QuantitySelector.init($(el)));
    } else {
      $('.js-qty-selector').map((i, el) => QuantitySelector.init($(el)));
    }
  }

  static getInputForProductCode(productCode: string, container: JQuery = $(document.body)): JQuery<HTMLInputElement> | null {
    const input = container.find<HTMLInputElement>(`.js-qty-selector-input[data-product-code="${productCode}"]`);
    if (input.length > 0) {
      return input;
    }
    return null;
  }

  static getInputForEntryNumber(entryNumber: string, container: JQuery = $(document.body)): JQuery<HTMLInputElement> | null {
    const input = container.find<HTMLInputElement>(`.js-qty-selector-input[data-entry-number="${entryNumber}"]`);
    if (input.length > 0) {
      return input;
    }
    return null;
  }

  static getValueForProductCode(productCode: string, container: JQuery = $(document.body)): number | null {
    const input = container.find(`.js-qty-selector-input[data-product-code="${productCode}"]`);
    if (input.length > 0) {
      return parseInt(input.val() as string, 10);
    }
    return null;
  }

  static getValueForEntryNumber(entryNumber: string, container: JQuery = $(document.body)): number | null {
    const input = container.find(`.js-qty-selector-input[data-entry-number="${entryNumber}"]`);
    if (input.length > 0) {
      return parseInt(input.val() as string, 10);
    }
    return null;
  }

  private allowsZeroValue() {
    return this.input.data('allow-zero') === true;
  }

  private isAtMinimumValue() {
    const value = this.input.val();
    if (this.allowsZeroValue()) {
      return value === 0;
    }

    return value === this.input.data('rounding-quantity');
  }

  private updateQtyValue(value: number): void {
    const roundingVal = parseInt(this.input.data('rounding-quantity') as string, 10) || 1;
    const max = this.input.data('max') as number;
    const oldValue = parseInt(this.input.val() as string, 10);
    let roundedValue = value;

    if (this.input.data('allow-zero') === true && value < 0) {
      roundedValue = 0;
    } else if (roundedValue < roundingVal && this.input.data('allow-zero') !== true) {
      roundedValue = roundingVal;
    } else if (roundedValue >= max) {
      roundedValue = max - (max % roundingVal);
    } else if (roundedValue % roundingVal !== 0) {
      roundedValue = Math.ceil(roundedValue / roundingVal) * roundingVal > max
        ? Math.floor(roundedValue / roundingVal) * roundingVal
        : Math.ceil(roundedValue / roundingVal) * roundingVal;
    }

    // These are left after porting ACC.productDetail.js, they sync the value with the hidden field in add to cart forms
    const addtocartQty = this.container.parents('.addtocart-component').find('#addToCartForm').find('.js-qty-selector-input');
    const configureQty = this.container.parents('.addtocart-component').find('#configureForm').find('.js-qty-selector-input');
    addtocartQty.val(roundedValue);
    configureQty.val(roundedValue);

    this.input.val(roundedValue);
    if (oldValue !== roundedValue) {
      this.input[0].dispatchEvent(new Event('input', { bubbles: true }));
      this.input[0].dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (roundedValue !== value) {
      this.showMessageContainer();
    } else {
      this.hideMessageContainer();
    }
  }

  private checkQtySelector(mode: string): void {
    const roundingVal = parseInt(this.input.data('rounding-quantity') as string, 10) || 1;
    let inputVal = parseInt((this.input.val() as string), 10);
    if (Number.isNaN(inputVal)) {
      inputVal = 0;
    }
    const max = this.input.data('max') as number;

    this.incrementButton.prop('disabled', false);
    this.decrementButton.prop('disabled', false);
    this.incrementButton.removeClass('-disabled');
    this.decrementButton.removeClass('-disabled');

    if (mode === 'minus') {
      this.updateQtyValue(inputVal - roundingVal);

      if ((inputVal - roundingVal === roundingVal && this.input.data('allow-zero') !== true) || inputVal - roundingVal === 0) {
        this.decrementButton.prop('disabled', 'disabled');
        this.decrementButton.addClass('-disabled');
      }
    } else if (mode === 'reset') {
      this.updateQtyValue(roundingVal);
    } else if (mode === 'plus') {
      this.updateQtyValue(inputVal + roundingVal);

      if (inputVal + (roundingVal * 2) > max) {
        this.incrementButton.prop('disabled', 'disabled');
        this.incrementButton.addClass('-disabled');
      }
    } else if (mode === 'blur') {
      this.updateQtyValue(inputVal);
      const currVal = parseInt(this.input.val() as string, 10);

      // eslint-disable-next-line no-mixed-operators
      if ((inputVal === roundingVal || inputVal < roundingVal) && this.input.data('allow-zero') !== true || currVal === 0) {
        this.decrementButton.prop('disabled', 'disabled');
        this.decrementButton.addClass('-disabled');
      }
      if (currVal + roundingVal > max) {
        this.incrementButton.prop('disabled', 'disabled');
        this.incrementButton.addClass('-disabled');
      }
    }

    this.input.trigger('check');
  }

  showMessageContainer(): void {
    this.messageContainer.show();
  }

  hideMessageContainer(): void {
    this.messageContainer.hide();
  }

  getInput(): JQuery {
    return this.input;
  }
}
